import { useCallback, useEffect } from 'react';

import { ActionKey, ActionSubjectIdKey, ActionSubjectKey } from '@/analytics/constants';
import { useFastshiftAnalyticsData } from '@/analytics/hooks/use-fastshift-analytics-data';
import { useFireAnalyticsEvents } from '@/analytics/use-fire-analytics-events';
import { useFastshiftSelector } from '@/state';

export const useOrganizationsListAnalytics = () => {
	const { sourceKey, identityAttrs, uiIdentityAttrs } = useFastshiftAnalyticsData();
	const { fireScreenEvent, fireTrackEvent } = useFireAnalyticsEvents(identityAttrs);
	const { fireUIEvent } = useFireAnalyticsEvents(uiIdentityAttrs);
	const { data, pending, error } = useFastshiftSelector((state) => state.organizations);

	useEffect(() => {
		if (identityAttrs === null) return;

		fireScreenEvent({ name: sourceKey });
	}, [fireScreenEvent, identityAttrs, sourceKey]);

	useEffect(() => {
		if (identityAttrs === null || pending || (!error && data === null)) {
			return;
		}

		fireTrackEvent({
			action: ActionKey.DISPLAYED,
			actionSubject: ActionSubjectKey.SECTION,
			actionSubjectId: ActionSubjectIdKey.ORGANIZATIONS_PAGE_DETAILS,
			source: sourceKey,
			attributes: error
				? { status: 'error' }
				: { status: data?.length ? 'normal' : 'empty', organizationCount: data?.length ?? 0 },
		});
	}, [data, error, pending, fireTrackEvent, identityAttrs, sourceKey]);

	const sendOrganizationCardClickUIEvent = useCallback(
		(selectedOrgId: string) => {
			fireUIEvent({
				action: ActionKey.CLICKED,
				actionSubject: ActionSubjectKey.LINK,
				actionSubjectId: ActionSubjectIdKey.ORGANIZATION_CARD,
				source: sourceKey,
				attributes: { selectedOrgId },
			});
		},
		[fireUIEvent, sourceKey],
	);

	return { sendOrganizationCardClickUIEvent };
};
